// All but popular were pre-built by ChatGPT
// Feel free to customize / adjust as needed

export const systems = [
  {
    role: "Customer Support Ops",
    systems: [
      {
        key: "support_ticket_mgmt",
        systemTitle: "Track support tickets",
        prompt:
          "Create a system to collect and manage support tickets from multiple platforms. The system should allow users to update ticket statuses, assign tickets to team members, and track progress through a visual board.",
      },
      {
        key: "customer_feedback_collection",
        systemTitle: "Collect customer feedback",
        prompt:
          "Build a system that collects and organizes customer feedback from various channels. Display feedback summaries, categorize by sentiment, and allow users to mark feedback for follow-up.",
      },
      {
        key: "auto_response_system",
        systemTitle: "Set up auto-responses",
        prompt:
          "Set up a system to automatically respond to common customer inquiries based on keywords. Provide an interface to manage response templates and update them as needed.",
      },
      {
        key: "escalation_workflow",
        systemTitle: "Escalate priority issues",
        prompt:
          "Develop a system that identifies and escalates high-priority or overdue support tickets according to pre-defined criteria. Include a review and manual escalation feature.",
      },
      {
        key: "sla_tracker",
        systemTitle: "Monitor SLA compliance",
        prompt:
          "Design a system to monitor service level agreement (SLA) compliance by tracking response times and ticket resolution. Highlight overdue tickets and display real-time SLA status.",
      },
    ],
  },
  {
    role: "Project Management",
    systems: [
      {
        key: "task_assignment_hub",
        systemTitle: "Distribute project tasks",
        prompt:
          "Create a system that distributes tasks based on team member workload and skills. Include a board view for team members to view, assign, and track task progress.",
      },
      {
        key: "project_status_dashboard",
        systemTitle: "Track project status",
        prompt:
          "Build a system to display project status across teams. Track milestones, deadlines, and progress, and display this information on a real-time dashboard.",
      },
      {
        key: "meeting_notes_organizer",
        systemTitle: "Organize meeting notes",
        prompt:
          "Set up a system to collect and organize meeting notes by project. Allow users to search, edit, and view notes based on specific topics or projects.",
      },
      {
        key: "risk_management_tracker",
        systemTitle: "Manage project risks",
        prompt:
          "Create a system that tracks project risks and their mitigation plans. Include a board or list view for users to add, update, and monitor risks.",
      },
      {
        key: "budget_resource_allocator",
        systemTitle: "Allocate project budgets",
        prompt:
          "Design a system to manage project budgets and resource allocations. Allow project managers to review and adjust resource distribution and monitor expenditures.",
      },
    ],
  },
  {
    role: "IT",
    systems: [
      {
        key: "incident_tracker",
        systemTitle: "Track IT incidents",
        prompt:
          "Build a system to track IT incidents and issues. Allow users to log incidents, assign team members for resolution, and track incident statuses through a dashboard.",
      },
      {
        key: "asset_management",
        systemTitle: "Organize IT assets",
        prompt:
          "Develop a system to manage IT assets. Track hardware and software inventory, assign assets to employees, and monitor maintenance schedules and warranties.",
      },
      {
        key: "access_request_manager",
        systemTitle: "Handle access requests",
        prompt:
          "Create a system to manage user access requests. Track requests for access to systems, assign approvals, and provide an overview of pending and completed requests.",
      },
      {
        key: "software_licensing_tracker",
        systemTitle: "Manage software licenses",
        prompt:
          "Design a system to track software licenses. Monitor expiration dates, renewal statuses, and compliance across the organization.",
      },
      {
        key: "security_incident_monitor",
        systemTitle: "Monitor security incidents",
        prompt:
          "Set up a system to monitor security incidents. Track vulnerabilities and breaches, assign team members for investigation, and provide real-time updates on incident resolution.",
      },
    ],
  },
  {
    role: "Business Owner",
    systems: [
      {
        key: "revenue_dashboard",
        systemTitle: "Monitor revenue streams",
        prompt:
          "Create a system that provides a real-time view of revenue streams, showing sales data, profit margins, and key financial metrics across various products and services.",
      },
      {
        key: "customer_retention_tracker",
        systemTitle: "Monitor customer retention",
        prompt:
          "Build a system to monitor customer retention rates, track churn, and identify patterns. Provide actionable insights and suggest strategies to improve customer retention.",
      },
      {
        key: "expense_tracker",
        systemTitle: "Track business expenses",
        prompt:
          "Design a system to track business expenses. Allow users to log and categorize expenses, monitor budget adherence, and generate monthly or quarterly expense reports.",
      },
      {
        key: "employee_performance_tracker",
        systemTitle: "Measure employee performance",
        prompt:
          "Create a system to track employee performance metrics, including sales targets, productivity, and KPIs. Provide managers with a view of individual and team performance.",
      },
      {
        key: "inventory_management",
        systemTitle: "Manage business inventory",
        prompt:
          "Build a system to manage inventory, track stock levels, and generate alerts when stock is low. Allow users to update inventory in real-time and generate reorder reports.",
      },
    ],
  },
  {
    role: "Marketing & Marketing Ops",
    systems: [
      {
        key: "campaign_performance_dashboard",
        systemTitle: "Monitor campaign performance",
        prompt:
          "Create a system to track the performance of marketing campaigns. Include metrics like clicks, conversions, and ROI, and provide a dashboard view of campaign progress.",
      },
      {
        key: "content_calendar",
        systemTitle: "Build a content calendar",
        prompt:
          "Develop a system to manage a marketing content calendar. Track upcoming posts, assign tasks to content creators, and monitor the publishing schedule.",
      },
      {
        key: "lead_nurturing_system",
        systemTitle: "Automate lead nurturing",
        prompt:
          "Set up a system to automate lead nurturing. Track lead progress through the funnel, send automated follow-ups, and score leads based on engagement and behavior.",
      },
      {
        key: "ad_spend_tracker",
        systemTitle: "Monitor ad spend",
        prompt:
          "Design a system to track ad spend and monitor performance across platforms. Compare costs, conversions, and return on ad spend for different campaigns.",
      },
      {
        key: "market_segmentation_manager",
        systemTitle: "Organize market segments",
        prompt:
          "Build a system to manage market segments. Organize customer data into segments, track engagement by segment, and provide actionable insights for targeting strategies.",
      },
    ],
  },
  {
    role: "HR & Recruiting Ops",
    systems: [
      {
        key: "candidate_database",
        systemTitle: "Manage candidate database",
        prompt:
          "Set up a system to manage candidate resumes and details. Include filters for sorting candidates by skills, and allow users to track candidates through the interview process.",
      },
      {
        key: "onboarding_tracker",
        systemTitle: "Monitor onboarding progress",
        prompt:
          "Build a system to track new employee onboarding tasks. Provide a progress view for HR to monitor task completion and ensure all onboarding steps are followed.",
      },
      {
        key: "leave_request_manager",
        systemTitle: "Manage leave requests",
        prompt:
          "Create a system to manage employee leave requests. Allow HR to approve or deny requests and track leave balances in an organized table.",
      },
      {
        key: "training_program_tracker",
        systemTitle: "Monitor training progress",
        prompt:
          "Develop a system to track employee training progress. Include a view to monitor completion rates and certifications, and provide options for scheduling additional training sessions.",
      },
      {
        key: "interview_scorecard",
        systemTitle: "Record interview feedback",
        prompt:
          "Create a system to record and store interviewer feedback and scores for job candidates. Allow HR to compare candidates and review scores before making final decisions.",
      },
    ],
  },
  {
    role: "Engineering",
    systems: [
      {
        key: "bug_tracker",
        systemTitle: "Log software bugs",
        prompt:
          "Create a system to track software bugs. Allow engineers to log, prioritize, and resolve bugs, and provide a dashboard view of bug statuses and priorities.",
      },
      {
        key: "code_review_manager",
        systemTitle: "Manage code reviews",
        prompt:
          "Build a system to manage code reviews. Assign reviews to team members, track review progress, and monitor code quality metrics.",
      },
      {
        key: "deployment_schedule",
        systemTitle: "Schedule software deployments",
        prompt:
          "Set up a system to schedule software deployments. Track upcoming releases, assign deployment tasks, and provide notifications for upcoming releases.",
      },
      {
        key: "devops_monitoring",
        systemTitle: "Track DevOps metrics",
        prompt:
          "Develop a system to monitor DevOps processes, track performance metrics, and provide real-time alerts for any system issues or slowdowns.",
      },
      {
        key: "technical_debt_tracker",
        systemTitle: "Track technical debt",
        prompt:
          "Create a system to track and prioritize technical debt. Log technical debt items, assign resolution tasks, and monitor progress toward reducing debt.",
      },
    ],
  },
  {
    role: "Data Science",
    systems: [
      {
        key: "model_experiment_tracker",
        systemTitle: "Monitor experiments over time",
        prompt:
          "Build a system to track data science experiments. Compare model configurations and performance metrics, and allow users to view and analyze results over time.",
      },
      {
        key: "data_pipeline_monitor",
        systemTitle: "Track pipeline performance",
        prompt:
          "Create a system to monitor data pipelines. Track pipeline health and detect issues in workflows, providing real-time updates and alerts for errors or delays.",
      },
      {
        key: "dataset_version_control",
        systemTitle: "Manage dataset versions",
        prompt:
          "Set up a system to manage dataset versions. Include a view to compare versions and changes, and provide options to revert to previous versions as needed.",
      },
      {
        key: "algorithm_comparison_dashboard",
        systemTitle: "Compare algorithm performance",
        prompt:
          "Develop a system to compare algorithm performance across multiple metrics such as accuracy and speed. Provide a dashboard view for side-by-side comparisons.",
      },
      {
        key: "automated_report_generator",
        systemTitle: "Generate automated reports",
        prompt:
          "Create a system that generates automated reports based on predefined datasets and parameters. Allow users to customize and download reports or view them on a dashboard.",
      },
    ],
  },
  {
    role: "Popular",
    systems: [
      {
        key: "lead_management",
        systemTitle: "Track sales leads",
        prompt:
          "I need a system to manage leads for my sales team. I want to track leads, assign them to sales reps, and see the status of each lead.",
      },
      {
        key: "simple_crm",
        systemTitle: "Manage customer relationships",
        prompt:
          "I need a simple CRM system to manage my customers. I want to track customer information, interactions, and sales.",
      },
      {
        key: "order_form",
        systemTitle: "Collect orders online",
        prompt:
          "I need an order form for my website. I want to collect customer information, products, and payment details.",
      },
      {
        key: "request_portal",
        systemTitle: "Manage team requests",
        prompt:
          "I need a request portal for my team. I want to collect requests, assign them to team members, and track the status of each request.",
      },
      {
        key: "faq_page",
        systemTitle: "Build an FAQ page",
        prompt:
          "I need an FAQ page for my website. I want to display common questions and answers for my customers.",
      },
      {
        key: "project_management_tool",
        systemTitle: "Track team projects",
        prompt:
          "I need a project management tool for my team. I want to track projects, assign tasks, and see the status of each project.",
      },
    ],
  },
];
